import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useJobs } from '../api/jobs';
import { useRuns } from '../api/runs';
import { StateBadge } from './StateBadge';
import { RelativeTime } from './RelativeTime';
import { NewJobModal } from './NewJobModal';
import type { Job, Run } from '../types';

// attentive-mobile is the default org for agent repos, so strip that owner prefix
// as noise — but keep the full owner/name for any other org so it isn't misleading.
const DEFAULT_OWNER_PREFIX = 'attentive-mobile/';

function displayRepo(repo: string): string {
  return repo.startsWith(DEFAULT_OWNER_PREFIX) ? repo.slice(DEFAULT_OWNER_PREFIX.length) : repo;
}

// The run to open for a RUNNING session: prefer the live RUNNING run, else the
// newest attempt. `undefined` while the runs list is still loading (the card
// falls back to the drawer until it resolves).
function currentRunId(runs: Run[] | undefined): string | undefined {
  if (!runs || runs.length === 0) return undefined;
  const live = runs.find((r) => r.state === 'RUNNING');
  if (live) return live.id;
  return runs
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0].id;
}

// Interactive sessions (long-lived dev-box jobs) as a card grid above the
// autonomous jobs table. Reads the same `useJobs` cache the table does (TanStack
// dedupes the request), filters to INTERACTIVE, and closes the grid with a
// "New interactive job" card that opens the new-job modal on its Interactive tab.
// Rendered by JobsListLayout above <JobsTable/>.
export function InteractiveSessions({ userId }: { userId: string }) {
  const { data } = useJobs(userId);
  const [newOpen, setNewOpen] = useState(false);

  // No pending/error branch: the new-job card is always useful, and JobsTable
  // (same query) owns the shared loading/error surface below.
  const sessions = (data ?? [])
    .filter((j) => j.mode === 'INTERACTIVE')
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  return (
    <section aria-label="Interactive sessions" className="px-6 pt-4">
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sessions.map((job) => (
          <li key={job.id}>
            <SessionCard job={job} />
          </li>
        ))}
        <li>
          <NewSessionCard onClick={() => setNewOpen(true)} />
        </li>
      </ul>
      {/* Mounted only while open so NewJobModal's initializer runs each time and
          the Interactive tab is preselected on open. */}
      {newOpen && <NewJobModal open initialKind="INTERACTIVE" onClose={() => setNewOpen(false)} />}
    </section>
  );
}

// A single interactive-session card. The card body is a link whose target
// depends on state: a RUNNING session opens its current run page (where the
// Connect panel lives); anything else opens the job drawer. The cog in the
// bottom-right always opens the drawer, and sits OUTSIDE the link (an absolute
// sibling) so a card body that's an <a> stays valid and the two clicks don't
// collide.
function SessionCard({ job }: { job: Job }) {
  const navigate = useNavigate();
  const running = job.state === 'RUNNING';
  // Only resolve runs for a RUNNING session; other states go straight to the drawer.
  const { data: runs } = useRuns(running ? job.id : undefined);
  const runId = running ? currentRunId(runs) : undefined;
  const primaryTo = runId ? `/jobs/${job.id}/runs/${runId}` : `/jobs/${job.id}`;
  const title = job.name || 'session';

  return (
    <div className="relative h-full">
      <Link
        to={primaryTo}
        className="flex h-full flex-col gap-2 rounded-lg border border-slate-200 bg-white p-3 pb-8 shadow-sm hover:border-slate-300 hover:shadow"
      >
        <div className="flex items-start justify-between gap-2">
          <span
            className="min-w-0 truncate font-medium text-slate-900"
            title={job.name ?? undefined}
          >
            {title}
          </span>
          <StateBadge state={job.state} />
        </div>
        {job.repo && (
          <span className="truncate text-xs text-slate-500" title={job.repo}>
            {displayRepo(job.repo)}
            {job.branch ? <span className="text-slate-400"> @{job.branch}</span> : null}
          </span>
        )}
        <span className="mt-auto text-xs text-slate-400">
          <RelativeTime iso={job.updatedAt} />
        </span>
      </Link>
      <button
        type="button"
        onClick={() => void navigate(`/jobs/${job.id}`)}
        aria-label={`Open details for ${title}`}
        title="Session details"
        className="absolute right-2 bottom-2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
      >
        <CogIcon />
      </button>
    </div>
  );
}

// The "New interactive job" affordance — a dashed card closing the grid that
// opens the new-job modal on its Interactive tab. The "+" is decorative; the
// button's accessible name is "New interactive job".
function NewSessionCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-full min-h-[5.5rem] w-full flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-white p-3 text-slate-500 hover:border-sky-500 hover:text-sky-600"
    >
      <span aria-hidden="true" className="text-2xl leading-none">
        +
      </span>
      <span className="text-sm font-medium">New interactive job</span>
    </button>
  );
}

// Feather "settings" gear, inlined (no icon dependency in this repo).
function CogIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.05a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.05a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}
